// Tuya Testing — desktop. Super Admin only.
//
// The same bench as the mobile screen: the devices that spoke to the broker most
// recently, regardless of owner, room or location, so a board flashed a moment
// ago is controllable before it belongs to anybody.
//
// The list, the subscriptions, the parsing and the commands all come from
// core/hooks/useTuyaBench, and the card from ui/shared/tuya — this file only
// decides how a wide screen lays them out. The two shells cannot drift.
//
// ── Why the layout is packed ───────────────────────────────────────────────
//
// A phone shows one card; a desktop shows a rack. The job here is SCANNING —
// which board is red, which has not reported, which is on the wrong type — so
// the cards run dense (see `dense` in TuyaDeviceCard) and the grid is
// auto-fill/minmax rather than fixed breakpoints: it fills a 4K screen with
// eight columns and a laptop with three, and never squeezes a card below the
// width its icon row needs. Fixed `md/lg/xl` columns did the opposite — sparse
// on the big screen it is used on, crushed on the small one.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Button, CircularProgress,
  TextField, MenuItem, Alert, Tooltip, Chip, Badge, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useApp } from '../../../core/state/AppContext';
import { useTuyaBench, TUYA_LIMITS } from '../../../core/hooks/useTuyaBench';
import { TUYA_SEEN_WINDOWS } from '../../../core/devices/tuyaTesting';
import TuyaDeviceCard from '../../shared/tuya/TuyaDeviceCard';
import DropLogDialog from '../../shared/tuya/DropLogDialog';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';

// The narrowest a dense card can be without its icon row wrapping.
const MIN_CARD = 320;

export default function TuyaTestingScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  // AllowTestingTuya (or Super Admin) — see AppContext's `canTuyaTest`.
  const { userId, canTuyaTest } = useApp();

  const [limit, setLimit] = useState(10);
  const [typeFilter, setTypeFilter] = useState(0);
  const [seenWithin, setSeenWithin] = useState(null); // ms, null = any
  const [updateOpen, setUpdateOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const bench = useTuyaBench({ userId, limit, deviceTypeFilter: typeFilter });

  // ⚠️ A FILTER, NOT A DELETION. The count in the header stays the FULL list, so
  // "Seen within 3 min" narrowing twelve devices to two never reads as ten
  // devices having vanished — which is exactly what someone hunting a dropout
  // would misread it as.
  const shown = useMemo(() => {
    const all = bench.rows || [];
    if (seenWithin == null) return all;
    return all.filter((r) => {
      const l = bench.liveness[String(r.UniqueNumber)];
      return l?.age != null && l.age <= seenWithin;
    });
  }, [bench.rows, bench.liveness, seenWithin]);

  // Memoised on the ROWS, not rebuilt per render: this screen re-renders on
  // every MQTT frame, and an inline scope literal would make the dialog reload
  // its liveness probe about once a second.
  const updateScope = useMemo(() => ({
    type: 'list',
    name: 'Tuya bench',
    devices: bench.rows || [],
  }), [bench.rows]);

  if (!canTuyaTest) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">This screen is for administrators.</Alert>
      </Box>
    );
  }

  const count = (bench.rows || []).length;

  const selectSx = {
    '& .MuiInputBase-input': { color: c.text, fontSize: 13, py: 0.5 },
    '& .MuiInput-underline:before': { borderBottomColor: 'currentColor', opacity: 0.4 },
    '& .MuiSvgIcon-root': { color: c.text },
  };

  return (
    <Box sx={{ bgcolor: c.back, minHeight: '100%' }}>
      <AppBar position="sticky" elevation={2}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <IconButton edge="start" size="small" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: 15, mr: 1 }}>TuyaTesting</Typography>

          {/* The counts are the Dart's, and they earn their place: they say how
              many devices ONE tap will move — which on a rack of boards is the
              thing worth knowing before pressing it. */}
          <Button
            size="small" variant="outlined" color="inherit" disabled={!count}
            startIcon={<PowerSettingsNewIcon />}
            onClick={bench.allOnEverything}
            sx={{ textTransform: 'none', borderRadius: 999, py: 0.15 }}
          >
            {`All On (${count})`}
          </Button>
          <Button
            size="small" variant="outlined" color="inherit" disabled={!count}
            startIcon={<PowerOffIcon />}
            onClick={bench.allOffEverything}
            sx={{ textTransform: 'none', borderRadius: 999, py: 0.15 }}
          >
            {`All Off (${count})`}
          </Button>

          {/* The third fleet action, and the only one that is not undoable — so
              unlike All On/All Off it does NOT fire on click. It opens the same
              confirmation the room and location rollouts use: one row per
              device, what firmware each is on, whether it even needs the update,
              and a tick box per row with select-all. Nothing is sent until the
              operator presses the button in the dialog. */}
          <Button
            size="small" variant="outlined" color="inherit" disabled={!count}
            startIcon={<SystemUpdateAltIcon />}
            onClick={() => setUpdateOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 999, py: 0.15 }}
          >
            {`Update Code (${count})`}
          </Button>

          <Box sx={{ flex: 1 }} />

          {/* The liveness summary, and the reason the screen ticks: these move
              on their own as devices go quiet, with nothing arriving to move
              them. `late` is one missed beat; `gone` is past the app's own
              ten-minute offline window. */}
          <Tooltip title="Heard from within the last 3½ minutes — one heartbeat plus slack">
            <Chip size="small" label={`live ${bench.counts.live}`} sx={{ height: 20, fontSize: 11, bgcolor: '#4CAF5033', color: '#A5D6A7' }} />
          </Tooltip>
          <Tooltip title="Missed a beat but under 10 minutes — usually still there">
            <Chip size="small" label={`late ${bench.counts.late}`} sx={{ height: 20, fontSize: 11, bgcolor: '#FFB74D33', color: '#FFCC80' }} />
          </Tooltip>
          <Tooltip title="Silent for over 10 minutes — offline by the app's own definition">
            <Chip size="small" label={`gone ${bench.counts.gone + bench.counts.never}`} sx={{ height: 20, fontSize: 11, bgcolor: '#E5393533', color: '#EF9A9A' }} />
          </Tooltip>

          <Tooltip title="When each device went quiet, and how long for">
            <span>
              <IconButton size="small" color="inherit" onClick={() => setLogOpen(true)}>
                <Badge
                  badgeContent={bench.dropEvents.filter((e) => e.kind === 'drop').length}
                  color="error"
                >
                  <LinkOffIcon fontSize="small" />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>

          {/* Client-side: it hides rows, it does not re-query. The heartbeat is
              every 3 minutes, so "3 min" is one beat — anything not in it has
              missed one. */}
          <TextField
            select variant="standard" value={seenWithin ?? ''} sx={{ ...selectSx, width: 96 }}
            onChange={(e) => setSeenWithin(e.target.value === '' ? null : Number(e.target.value))}
          >
            {TUYA_SEEN_WINDOWS.map((w) => (
              <MenuItem key={w.label} value={w.ms ?? ''} sx={{ fontSize: 13 }}>
                {w.ms == null ? 'Seen: any' : `Seen: ${w.label}`}
              </MenuItem>
            ))}
          </TextField>

          {/* Both are request parameters the endpoint already takes — the Dart
              hardcodes LimitCount 10 and DeviceID 0. */}
          <TextField
            select variant="standard" value={limit} sx={{ ...selectSx, width: 64 }}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {TUYA_LIMITS.map((n) => <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>{n}</MenuItem>)}
          </TextField>
          <TextField
            select variant="standard" value={typeFilter} sx={{ ...selectSx, width: 160 }}
            onChange={(e) => setTypeFilter(Number(e.target.value))}
          >
            <MenuItem value={0} sx={{ fontSize: 13 }}>All types</MenuItem>
            {bench.types.map((t) => (
              <MenuItem key={t.DeviceID} value={Number(t.DeviceID)} sx={{ fontSize: 13 }}>
                {t.DeviceName}
              </MenuItem>
            ))}
          </TextField>

          <Tooltip title="Reload the list">
            <span>
              <IconButton size="small" color="inherit" onClick={bench.reload} disabled={bench.loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 1.5 }}>
        {bench.rows === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {bench.error && <Alert severity="error" sx={{ mb: 2 }}>{bench.error}</Alert>}
        {bench.rows !== null && !bench.rows.length && !bench.error && (
          <Typography sx={{ color: `${c.text}99`, textAlign: 'center', py: 6 }}>
            No devices have reported.
          </Typography>
        )}
        {/* Says the list was FILTERED, never just goes empty. An empty bench and
            a filtered-out bench look identical, and only one of them is a
            problem worth chasing. */}
        {bench.rows !== null && bench.rows.length > 0 && shown.length === 0 && (
          <Typography sx={{ color: `${c.text}99`, textAlign: 'center', py: 6 }}>
            {`None of the ${bench.rows.length} devices has been heard from that recently — widen "Seen".`}
          </Typography>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_CARD}px, 1fr))`,
            alignItems: 'start',
          }}
        >
          {shown.map((row) => (
            <TuyaDeviceCard
              key={row.UniqueNumber}
              dense
              row={row}
              types={bench.types}
              values={bench.values[String(row.UniqueNumber)] || []}
              message={bench.messages[String(row.UniqueNumber)] || ''}
              live={bench.liveness[String(row.UniqueNumber)]}
              onToggleChannel={bench.toggleChannel}
              onFanSpeed={bench.setFanSpeed}
              onAllOn={bench.allOn}
              onAllOff={bench.allOff}
              onUnlock={bench.unlock}
              onDeviceDetails={bench.deviceDetails}
              onUpdateCode={bench.updateCode}
              onChangeType={bench.changeType}
            />
          ))}
        </Box>
      </Box>

      <UpdateCodeDialog
        open={updateOpen}
        scope={updateScope}
        onClose={() => setUpdateOpen(false)}
      />

      <DropLogDialog
        open={logOpen}
        events={bench.dropEvents}
        onClear={bench.clearDropEvents}
        onClose={() => setLogOpen(false)}
      />
    </Box>
  );
}
